import { PreviewMediaSession, type PreviewSessionEventHandlers } from '@yandex-360/telemost-sdk/device-manager';
import type {
    ActiveMediaDevices,
    MediaDeviceQuery,
    MediaSessionConfig,
    MediaSoundProcessorOptions,
    MediaVideoProcessorOptions,
    QueryMediaDevices,
    RequestedMediaDevices,
    SelectedMediaDevices,
} from '@yandex-360/telemost-sdk/device-manager';
import { Session, SessionEvent } from '@yandex-video-platform/goloom-sdk';

import { logUnsupportedSoundProcessorOptionsForGoloom } from './goloom-sound-processor-diagnostics';
import {
    mapActiveDevicesChangedFromGoloom,
    mapActiveDevicesFromGoloom,
    mapDeviceChangedFromGoloom,
    mapDeviceToGoloom,
    mapRequestedDevicesFromGoloom,
    mapSelectedDevicesFromGoloom,
    mapSessionConfigToGoloom,
    mapSoundProcessorToGoloom,
    mapVideoProcessorToGoloom,
} from './goloom-type-mappers';

const emptyRequestedDevices: RequestedMediaDevices = {
    audioInput: [],
    videoInput: [],
    audioOutput: [],
};

type WrappedPreviewHandlers = {
    onActiveDevicesChanged: (event: SessionEvent['activeDevicesChanged']) => void | Promise<void>;
    onDeviceChanged: (event: SessionEvent['deviceChanged']) => void | Promise<void>;
};

export class GoloomPreviewSession extends PreviewMediaSession {
    private session?: Session;
    private config?: MediaSessionConfig;
    private readonly wrappedHandlers = new Map<PreviewSessionEventHandlers, WrappedPreviewHandlers>();

    isInitialized(): boolean {
        return Boolean(this.session);
    }

    getConfig(): Maybe<MediaSessionConfig> {
        return this.config;
    }

    updateConfig(config: MediaSessionConfig): void {
        this.config = config;
    }

    create(config: MediaSessionConfig): void {
        this.config = config;

        const goloomConfig = mapSessionConfigToGoloom(config);

        if (config.soundProcessing && goloomConfig.soundProcessing) {
            logUnsupportedSoundProcessorOptionsForGoloom(
                config.soundProcessing,
                goloomConfig.soundProcessing,
                'GoloomPreviewSession.create'
            );
        }

        this.session = new Session(goloomConfig);
    }

    async destroy(): Promise<void> {
        if (!this.session) {
            return;
        }

        const prevSession = this.session;
        await prevSession.destroy();

        if (prevSession === this.session) {
            this.session = undefined;
        }
    }

    async requestDevices(query?: QueryMediaDevices): Promise<RequestedMediaDevices> {
        if (!this.session) {
            return emptyRequestedDevices;
        }

        return mapRequestedDevicesFromGoloom(await this.session.requestDevices(query));
    }

    async selectDevice(device: MediaDeviceQuery): Promise<void> {
        await this.session?.selectDevice(mapDeviceToGoloom(device));
    }

    async applySelectedDevices(): Promise<void> {
        await this.session?.applySelectedDevices();
    }

    async setAudioStatus(status: boolean): Promise<unknown> {
        return this.session?.setAudioStatus(status);
    }

    async setVideoStatus(status: boolean): Promise<unknown> {
        return this.session?.setVideoStatus(status);
    }

    async setVideoProcessorOptions(options: MediaVideoProcessorOptions): Promise<void> {
        await this.session?.setVideoProcessorOptions(mapVideoProcessorToGoloom(options));
    }

    async setSoundProcessorOptions(options: MediaSoundProcessorOptions): Promise<void> {
        const goloomOptions = mapSoundProcessorToGoloom(options);

        logUnsupportedSoundProcessorOptionsForGoloom(
            options,
            goloomOptions,
            'GoloomPreviewSession.setSoundProcessorOptions'
        );

        await this.session?.setSoundProcessorOptions(goloomOptions);
    }

    get activeDevices(): ActiveMediaDevices {
        return this.session
            ? mapActiveDevicesFromGoloom(this.session.activeDevices)
            : { audioInput: null, videoInput: null, audioOutput: null };
    }

    get selectedDevices(): SelectedMediaDevices {
        return this.session
            ? mapSelectedDevicesFromGoloom(this.session.selectedDevices)
            : { audioInput: null, videoInput: null, audioOutput: null };
    }

    subscribe(handlers: PreviewSessionEventHandlers): void {
        if (!this.session || this.wrappedHandlers.has(handlers)) {
            return;
        }

        const wrapped: WrappedPreviewHandlers = {
            onActiveDevicesChanged: (event) =>
                handlers.onActiveDevicesChanged(mapActiveDevicesChangedFromGoloom(event)),
            onDeviceChanged: (event) => handlers.onDeviceChanged(mapDeviceChangedFromGoloom(event)),
        };

        this.wrappedHandlers.set(handlers, wrapped);
        this.session.addAsyncEventListener('activeDevicesChanged', wrapped.onActiveDevicesChanged);
        this.session.addAsyncEventListener('deviceChanged', wrapped.onDeviceChanged);
    }

    unsubscribe(handlers: PreviewSessionEventHandlers): void {
        const wrapped = this.wrappedHandlers.get(handlers);

        if (!this.session || !wrapped) {
            return;
        }

        this.session.removeAsyncEventListener('activeDevicesChanged', wrapped.onActiveDevicesChanged);
        this.session.removeAsyncEventListener('deviceChanged', wrapped.onDeviceChanged);
        this.wrappedHandlers.delete(handlers);
    }
}
